import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import {
  Text,
  ConfirmationResultModal,
  Checkbox,
  Spacer,
} from '@percent/lemonade'
import { APIErrorHandler, API_ERROR_MESSAGE } from '@percent/utility'
import type { Reset2FAModalProps } from './Reset2FAModal.types'

export function Reset2FAModal({
  isOpen,
  onClose,
  onSuccess,
  onReset,
  account,
}: Reset2FAModalProps) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [idConfirmed, setIdConfirmed] = useState(false)

  const name = account.fullName ?? account.email

  const state =
    status === 'success'
      ? 'success'
      : status === 'error'
      ? 'failure'
      : undefined
  const isPending = status === 'loading'

  const handleConfirm = async () => {
    setStatus('loading')
    setErrorMessage(undefined)
    try {
      await onReset(account.id)
      setStatus('success')
      onSuccess?.()
    } catch (err) {
      setStatus('error')
      const axiosError = err as AxiosError<{ error: Record<string, unknown> }>
      const apiError = APIErrorHandler(axiosError?.response?.data?.error)
      setErrorMessage(
        apiError === API_ERROR_MESSAGE.genericError
          ? t('modal.accounts.reset2FA.error.description', { account: name })
          : apiError
      )
    }
  }

  const handleClose = () => {
    if (status !== 'loading') {
      setStatus('idle')
      setErrorMessage(undefined)
      setIdConfirmed(false)
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      success={{
        title: t('modal.accounts.reset2FA.success.title'),
        description: t('modal.accounts.reset2FA.success.description', {
          account: name,
        }),
        button: t('modal.accounts.reset2FA.close'),
      }}
      failure={{
        title: t('modal.accounts.reset2FA.error.title'),
        description: errorMessage,
        button: t('modal.accounts.reset2FA.close'),
      }}
      title={t('modal.accounts.reset2FA.confirm.title')}
      primaryButtonText={t('modal.accounts.reset2FA.reset')}
      secondaryButtonText={t('modal.accounts.reset2FA.cancel')}
      primaryBtnTestId="reset-2fa-submit-button"
      secondaryBtnTestId="reset-2fa-cancel-button"
      buttonTestId="reset-2fa-close-button"
      state={state}
      loading={isPending}
      disabled={!idConfirmed}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Text>
        {t('modal.accounts.reset2FA.confirm.description', { account: name })}
      </Text>
      <Spacer size={8} axis="vertical" />
      <Checkbox
        name="checkID"
        label={t('modal.accounts.reset2FA.confirm.checkbox')}
        variant="default"
        dataTestId="reset-2fa-id-checkbox"
        active={idConfirmed}
        necessity="required"
        onChange={(e) => setIdConfirmed((e.target as HTMLInputElement).checked)}
      />
    </ConfirmationResultModal>
  )
}
